import api from '@/api/axios';
import type { BffApiResponse } from '@/types';

export interface GroupResponse {
  id: string;
  name: string;
  path: string;
  attributes?: Record<string, string>;
}

export interface GroupUserResponse {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  enabled: boolean;
}

export interface CreateGroupRequest {
  name: string;
  attributes?: Record<string, string>;
}

export interface UpdateGroupRequest {
  name?: string;
  attributes?: Record<string, string>;
}

class GroupsService {
  private readonly basePath = '/groups';

  async getAllGroups(): Promise<BffApiResponse<GroupResponse[]>> {
    const response = await api.get<BffApiResponse<GroupResponse[]>>(this.basePath);
    return response.data;
  }

  async getGroupById(groupId: string): Promise<BffApiResponse<GroupResponse>> {
    const response = await api.get<BffApiResponse<GroupResponse>>(
      `${this.basePath}/${groupId}`
    );
    return response.data;
  }

  async createGroup(data: CreateGroupRequest): Promise<BffApiResponse<GroupResponse>> {
    const response = await api.post<BffApiResponse<GroupResponse>>(
      this.basePath,
      data
    );
    return response.data;
  }

  async updateGroup(
    groupId: string,
    data: UpdateGroupRequest
  ): Promise<BffApiResponse<GroupResponse>> {
    const response = await api.put<BffApiResponse<GroupResponse>>(
      `${this.basePath}/${groupId}`,
      data
    );
    return response.data;
  }

  async deleteGroup(groupId: string): Promise<BffApiResponse<null>> {
    const response = await api.delete<BffApiResponse<null>>(
      `${this.basePath}/${groupId}`
    );
    return response.data;
  }

  async getGroupMembers(groupId: string): Promise<BffApiResponse<GroupUserResponse[]>> {
    const response = await api.get<BffApiResponse<GroupUserResponse[]>>(
      `${this.basePath}/${groupId}/members`
    );
    return response.data;
  }

  async addUserToGroup(groupId: string, userId: string): Promise<BffApiResponse<null>> {
    const response = await api.put<BffApiResponse<null>>(
      `${this.basePath}/${groupId}/members/${userId}`
    );
    return response.data;
  }

  async removeUserFromGroup(groupId: string, userId: string): Promise<BffApiResponse<null>> {
    const response = await api.delete<BffApiResponse<null>>(
      `${this.basePath}/${groupId}/members/${userId}`
    );
    return response.data;
  }
}

export const groupsService = new GroupsService();
export default groupsService;